import api from '@/api/classes';
import lessonsApi from '@/api/lessons';
import teacherAdminApi from '@/api/teacher-admin';
import { CLASS_TYPES } from '@/constants';

const actions = {
  FETCH_CLASS_DETAIL: async ({ commit, dispatch, rootState }, { id, type }) => {
    if (!id || !type) {
      return;
    }

    dispatch('wait/start', 'classes.fetchingDetail', { root: true });

    try {
      const route = rootState.route.name;
      const detail = await api.fetchClassDetail({ id, type });

      commit('SET_CLASS_DETAIL', detail);
      commit('SET_REFRESH_DATE', Date.now());

      const { class_info: { data_timezone: timeZone } = {} } = detail;

      if (timeZone) {
        commit('timezones/SET_ROUTE_TIMEZONE', { route, timeZone }, { root: true });
      }
    } finally {
      dispatch('wait/end', 'classes.fetchingDetail', { root: true });
    }
  },

  FETCH_LEARNER_ASSESSMENT: async ({ dispatch }, { learnerId, assessmentId }) => {
    dispatch('wait/start', 'classes.fetchingAssessment', { root: true });

    try {
      return await api.fetchLearnerAssessment(learnerId, assessmentId);
    } finally {
      dispatch('wait/end', 'classes.fetchingAssessment', { root: true });
    }
  },

  CANCEL_CLASS: async ({ commit, dispatch }, { id, type }) => {
    if (!id || !type) {
      return;
    }

    dispatch('wait/start', 'classes.cancellingClass', { root: true });

    try {
      if (type === CLASS_TYPES.GROUP) {
        await api.cancelGroupClass(id);
      }

      if (type === CLASS_TYPES.PRIVATE) {
        await api.cancelPrivateClass(id);
        // Fetch fresh class details after cancellation instead of using the return value
        const refreshedClass = await api.fetchClassDetail({ id, type });
        commit('SET_CLASS_DETAIL', refreshedClass);
      }
    } finally {
      dispatch('wait/end', 'classes.cancellingClass', { root: true });
    }
  },

  MARK_NO_SHOW: async ({ dispatch }, { id }) => {
    if (!id) {
      return;
    }

    dispatch('wait/start', 'classes.markingNoShow', { root: true });

    const feedbackData = {
      no_show: true,
    };

    try {
      await api.submitPrivateClassFeedback(id, feedbackData);
    } finally {
      dispatch('wait/end', 'classes.markingNoShow', { root: true });
    }
  },

  CREATE_GROUP_CLASS: async ({ dispatch }, classData) => {
    dispatch('wait/start', 'classes.saving', { root: true });

    try {
      await teacherAdminApi.createGroupClass(classData);
    } finally {
      dispatch('wait/end', 'classes.saving', { root: true });
    }
  },

  UPDATE_GROUP_CLASS: async ({ commit, dispatch }, classData) => {
    if (!classData.id) {
      return;
    }
    dispatch('wait/start', 'classes.saving', { root: true });

    try {
      const response = await api.saveGroupClass(classData);

      if (response.class_info && response.warnings) {
        commit('SET_CLASS_INFO', response.class_info);
        commit('SET_CLASS_WARNINGS', response.warnings);
      } else {
        const refreshedClass = await api.fetchClassDetail({ id: classData.id, type: CLASS_TYPES.GROUP });
        commit('SET_CLASS_DETAIL', refreshedClass);
      }
    } finally {
      dispatch('wait/end', 'classes.saving', { root: true });
    }
  },

  UPDATE_PRIVATE_CLASS: async ({ commit, dispatch }, classData) => {
    if (!classData.id) {
      return;
    }

    dispatch('wait/start', 'classes.saving', { root: true });

    try {
      await api.savePrivateClass(classData);
      const refreshedClass = await api.fetchClassDetail({ id: classData.id, type: CLASS_TYPES.PRIVATE });
      commit('SET_CLASS_DETAIL', refreshedClass);
    } finally {
      dispatch('wait/end', 'classes.saving', { root: true });
    }
  },

  SAVE_PRIVATE_CLASS_FEEDBACK: async ({ commit, dispatch }, { id, feedback }) => {
    if (!id) {
      return;
    }

    dispatch('wait/start', 'classes.saving', { root: true });

    try {
      await api.savePrivateClassFeedback(id, feedback);
      const refreshedClass = await api.fetchClassDetail({ id, type: CLASS_TYPES.PRIVATE });
      commit('SET_CLASS_DETAIL', refreshedClass);
    } finally {
      dispatch('wait/end', 'classes.saving', { root: true });
    }
  },

  SUBMIT_PRIVATE_CLASS_FEEDBACK: async ({ commit, dispatch }, { id, feedback }) => {
    if (!id) {
      return;
    }

    dispatch('wait/start', 'classes.saving', { root: true });

    try {
      await api.submitPrivateClassFeedback(id, feedback);
      const refreshedClass = await api.fetchClassDetail({ id, type: CLASS_TYPES.PRIVATE });
      commit('SET_CLASS_DETAIL', refreshedClass);
    } finally {
      dispatch('wait/end', 'classes.saving', { root: true });
    }
  },

  VALIDATE_LESSON: async ({ dispatch }, lessonId) => {
    if (!lessonId) {
      return;
    }

    dispatch('wait/start', 'classes.validatingLesson', { root: true });

    try {
      // eslint-disable-next-line consistent-return
      return await api.validateLesson(lessonId);
    } finally {
      dispatch('wait/end', 'classes.validatingLesson', { root: true });
    }
  },

  CANCEL_SHARED_LESSON: async ({ commit, dispatch }, lessonId) => {
    if (!lessonId) {
      return;
    }

    dispatch('wait/start', 'classes.cancelSharedLesson', { root: true });

    try {
      await lessonsApi.cancelSharedLesson(lessonId);
      commit('REMOVE_SHARED_LESSON');
    } finally {
      dispatch('wait/end', 'classes.cancelSharedLesson', { root: true });
    }
  },

  REFUND_CLASS: async ({ dispatch }, { id }) => {
    if (!id) {
      return;
    }
    dispatch('wait/start', 'classes.refundingClass', { root: true });
    try {
      await api.refundClass(id);
    } finally {
      dispatch('wait/end', 'classes.refundingClass', { root: true });
    }
  },

  UPLOAD_CLASSES: async (_, { file }) => {
    return api.uploadClasses({ file });
  },
};

export default actions;
